import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useMfeOptions } from './MfeOptionsContext';
import {
  ErrorCodes,
  ErrorType,
  UserTenantOnboardingError
} from 'src/types/error';
import { usePublishRumEvent } from 'src/hooks/usePublishRumEvent';
import { useFinishExecution } from 'src/hooks/useFinishExecution';
import { Result } from 'src/types/result';
import { Logger } from 'src/utils/Logger';
import { publishEvent } from 'src/analytics/publishEvent';
import { BackFromHpIdType, ScreenAction } from 'src/analytics/types';

type ErrorContextType = {
  error?: UserTenantOnboardingError;
  authError: string | null;
  isCustomError: boolean;
  updateError: (error?: UserTenantOnboardingError) => void;
  handleAuthError: (errorMessage: string) => void;
};

type ErrorProviderProps = {
  children: React.ReactNode;
};

export const ErrorContext = createContext({} as ErrorContextType);

export const ErrorProvider = ({ children }: ErrorProviderProps) => {
  const {
    options: { errorMfeAssetReference }
  } = useMfeOptions();
  const { finishOnError } = useFinishExecution();
  const { publishRumEvent } = usePublishRumEvent();

  const [error, setError] = useState<UserTenantOnboardingError>(null);

  const isCustomError = useMemo(
    () => Boolean(errorMfeAssetReference),
    [errorMfeAssetReference]
  );

  const authError = useMemo(() => {
    const mySearchParams = new URLSearchParams(window.location.search);
    return mySearchParams.get('error_type');
  }, []);

  const updateError = useCallback(
    (newError: UserTenantOnboardingError | null) => {
      setError((prev) => {
        Logger.error('Error updated:', { oldError: prev, newError });

        const isNewError =
          prev?.errorCode !== newError?.errorCode ||
          prev?.message !== newError?.message;

        if (isNewError) {
          publishRumEvent(newError);
          Logger.error('Error updated', { oldError: prev, newError });
          return newError;
        }

        return prev;
      });
    },
    [publishRumEvent]
  );

  const closeWithoutConfirmation = useCallback(
    (errorMessage: string) => {
      const finishError = {
        errorCode: ErrorCodes.UserCanceledError,
        message: errorMessage
      };

      publishEvent({
        type: ScreenAction.BACK_FROM_HP_ID,
        payload: {
          actionAuxParams: `status=${BackFromHpIdType.NOT_SIGNED_IN}`
        }
      });

      publishRumEvent(finishError);
      finishOnError(Result.Declined, finishError);
    },
    [publishRumEvent, finishOnError]
  );

  const handleAuthError = useCallback(
    (errorMessage: string) => {
      // If user cancels (tryAgain or closing the auth view) close automatically
      // We don't need to consider the initialization here, since the UserCanceled is specific to non-portal (non-WEB_PORTAL) flows
      if (errorMessage === ErrorType.UserCanceled) {
        closeWithoutConfirmation(errorMessage);
      }
      // Else, shows the confirmation UI to notify User of error
      // portal (WEB_PORTAL) flows will always end up here
      else {
        updateError({
          errorCode: ErrorCodes.LoginError,
          message: errorMessage
        });
      }
    },
    [updateError, closeWithoutConfirmation]
  );

  useEffect(() => {
    Logger.log({ authError, isCustomError });

    if (authError) {
      handleAuthError(authError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authError, handleAuthError]);

  const value = useMemo(
    () => ({ error, authError, isCustomError, updateError, handleAuthError }),
    [error, authError, isCustomError, updateError, handleAuthError]
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
};

export const useErrorContext = () => useContext(ErrorContext);
